import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { 
  ArrowLeft, Plus, FileText, DollarSign, CheckCircle, Clock, 
  Send, Download, Eye, Trash2, MoreVertical, Building2, Receipt,
  Calendar, PenTool, Landmark, Phone, Mail, MapPin,
  CheckCircle2, XCircle, User, Edit2, Save, AlertCircle,
  Upload, FileUp, Bell, Printer, ExternalLink, Paperclip, X,
  ClipboardList, TrendingUp
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import BlueprintIndicator from '@/components/Blueprint/BlueprintIndicator';
import { DragDropUpload } from '@/components/ui/DragDropUpload';
import { 
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue 
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from 'sonner';
import HelpIcon from '@/components/HelpIcon';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_CONFIG = {
  draft: { label: 'Draft', color: 'bg-gray-100 text-gray-700', icon: Edit2 },
  pending_signature: { label: 'Pending Signature', color: 'bg-amber-100 text-amber-700', icon: PenTool },
  signed: { label: 'Signed', color: 'bg-blue-100 text-blue-700', icon: CheckCircle },
  submitted_to_title: { label: 'At Title Co.', color: 'bg-purple-100 text-purple-700', icon: Landmark },
  funded: { label: 'Funded', color: 'bg-green-100 text-green-700', icon: DollarSign },
};

export default function DrawRequestsPage() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [receivedInvoices, setReceivedInvoices] = useState([]);
  
  const [project, setProject] = useState(null);
  const [drawRequests, setDrawRequests] = useState([]);
  const [summary, setSummary] = useState({});
  const [invoices, setInvoices] = useState([]);
  const [titleCompanies, setTitleCompanies] = useState([]);
  const [contractors, setContractors] = useState([]); // For linking payees to contractors
  const [loading, setLoading] = useState(true);
  const [defaultDisclaimer, setDefaultDisclaimer] = useState('');
  const [activeTab, setActiveTab] = useState('draws');
  
  // Pay Reports state
  const [payReports, setPayReports] = useState([]);
  const [payReportsSummary, setPayReportsSummary] = useState({});
  const [showPayReportDialog, setShowPayReportDialog] = useState(false);
  const [newPayReport, setNewPayReport] = useState({
    title_company_id: '',
    title_company_name: '',
    report_date: new Date().toISOString().split('T')[0],
    report_number: '',
    original_loan_amount: '',
    total_disbursed: '',
    remaining_balance: '',
    loan_status: 'active',
    draw_request_id: '',
    total_this_report: '',
    document_url: '',
    document_name: '',
    notes: ''
  });
  
  // Dialogs
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showTitleCompanyDialog, setShowTitleCompanyDialog] = useState(false);
  const [showSignDialog, setShowSignDialog] = useState(false);
  const [showPreviewDialog, setShowPreviewDialog] = useState(false);
  const [showUploadSignedDialog, setShowUploadSignedDialog] = useState(false);
  const [selectedDraw, setSelectedDraw] = useState(null);
  const [previewHtml, setPreviewHtml] = useState('');
  
  // New draw request form
  const [newDraw, setNewDraw] = useState({
    title_company_id: '',
    payment_type: 'title_company',
    payees: [],
    notes: '',
    disclaimer: '',
    owner_name: '',
    contractor_name: '',
    contractor_title: ''
  });
  
  // New title company form
  const [newTitleCompany, setNewTitleCompany] = useState({
    name: '', address: '', city: '', state: '', zip_code: '',
    phone: '', fax: '', email: '', lender_name: '', is_default: false
  });
  
  // Signature form
  const [signatureData, setSignatureData] = useState({
    role: 'owner',
    name: '',
    signature: '',
    title: ''
  });
  
  // Uploaded signed PDF
  const [uploadedSignedUrl, setUploadedSignedUrl] = useState('');
  
  const darkMode = document.documentElement.classList.contains('dark');
  
  useEffect(() => {
    fetchData();
    fetchDefaultDisclaimer();
  }, [projectId]);

  useEffect(() => {
    const ri = location.state?.preSelectedInvoice;
    if (!ri) return;
    addFromReceivedInvoice(ri);
    setShowCreateDialog(true);
    navigate(location.pathname, { replace: true, state: null });
  }, [location.state]); // eslint-disable-line react-hooks/exhaustive-deps
  
  const fetchData = async () => {
    try {
      setLoading(true);
      const [projectRes, drawsRes, titleRes, payReportsRes, contractorsRes] = await Promise.all([
        axios.get(`${API}/projects/${projectId}`),
        axios.get(`${API}/projects/${projectId}/draw-requests`),
        axios.get(`${API}/title-companies`),
        axios.get(`${API}/projects/${projectId}/title-company-reports`).catch(() => ({ data: { reports: [], summary: {} } })),
        axios.get(`${API}/contractors`).catch(() => ({ data: [] }))
      ]);
      
      setProject(projectRes.data);
      setDrawRequests(drawsRes.data.draw_requests || []);
      setSummary(drawsRes.data.summary || {});
      setTitleCompanies(titleRes.data.title_companies || []);
      setPayReports(payReportsRes.data.reports || []);
      setPayReportsSummary(payReportsRes.data.summary || {});
      setContractors(contractorsRes.data || []);
      
      // Pre-fill owner name from project client if available
      if (projectRes.data?.client?.name) {
        setNewDraw(prev => ({ ...prev, owner_name: projectRes.data.client.name }));
      }
      
      // Try to get invoices
      try {
        const invoicesRes = await axios.get(`${API}/invoices`, {
          params: { project_id: projectId }
        });
        setInvoices(invoicesRes.data.invoices || invoicesRes.data || []);
      } catch (e) {
        setInvoices([]);
      }
      try {
        const riRes = await axios.get(`${API}/projects/${projectId}/received-invoices`);
        setReceivedInvoices((riRes.data.received_invoices || []).filter((r) => ['pending_review', 'approved'].includes(r.status)));
      } catch (e) { setReceivedInvoices([]); }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load draw requests');
    } finally {
      setLoading(false);
    }
  };
  
  const fetchDefaultDisclaimer = async () => {
    try {
      const res = await axios.get(`${API}/company-settings/draw-request-disclaimer`);
      setDefaultDisclaimer(res.data.disclaimer || '');
    } catch (e) {
      console.error('Error fetching disclaimer:', e);
    }
  };
  
  const createDrawRequest = async () => {
    try {
      if (newDraw.payees.length === 0) {
        toast.error('Please add at least one payee/invoice');
        return;
      }
      
      // Use default disclaimer if not customized
      const drawData = {
        ...newDraw,
        disclaimer: newDraw.disclaimer || defaultDisclaimer
      };
      
      await axios.post(`${API}/projects/${projectId}/draw-requests`, drawData);
      toast.success('Draw Request created');
      setShowCreateDialog(false);
      setNewDraw({ 
        title_company_id: '', 
        payment_type: 'title_company', 
        payees: [], 
        notes: '',
        disclaimer: '',
        owner_name: project?.client?.name || '',
        contractor_name: '',
        contractor_title: ''
      });
      fetchData();
    } catch (error) {
      console.error('Error creating draw:', error);
      toast.error('Failed to create draw request');
    }
  };
  
  const createTitleCompany = async () => {
    try {
      await axios.post(`${API}/title-companies`, newTitleCompany);
      toast.success('Title Company added');
      setShowTitleCompanyDialog(false);
      setNewTitleCompany({ name: '', address: '', city: '', state: '', zip_code: '', phone: '', fax: '', email: '', lender_name: '', is_default: false });
      fetchData();
    } catch (error) {
      toast.error('Failed to add title company');
    }
  };
  
  const addPayeeRow = () => {
    setNewDraw({
      ...newDraw,
      payees: [...newDraw.payees, {
        pay_to: '',
        contact_name: '',
        mail_address: '',
        city: '',
        state: '',
        zip_code: '',
        phone: '',
        email: '',
        delivery_method: 'mail',
        description: '',
        amount: 0,
        attached_invoice_url: '',
        attached_invoice_name: '',
        attached_invoice_type: '',
        estimate_id: '',
        has_subcontractors: false,
        subcontractor_names: '',
        lien_waiver_attached: false,
        receipt_attached: false,
        contractor_id: '' // Link to contractor for insurance audit
      }]
    });
  };
  
  // Handle selecting a contractor to auto-populate payee fields
  const selectContractorForPayee = (index, contractorId) => {
    if (!contractorId) {
      // Clear contractor link
      updatePayee(index, 'contractor_id', '');
      return;
    }
    
    const contractor = contractors.find(c => c.id === contractorId);
    if (!contractor) return;
    
    // Auto-populate payee fields from contractor
    const updated = [...newDraw.payees];
    updated[index] = {
      ...updated[index],
      contractor_id: contractorId,
      pay_to: contractor.company_name || '',
      contact_name: contractor.contact_name || '',
      phone: contractor.phone || contractor.cell_phone || '',
      email: contractor.email || '',
      mail_address: contractor.address?.street || contractor.address || '',
      city: contractor.address?.city || '',
      state: contractor.address?.state || '',
      zip_code: contractor.address?.zip || ''
    };
    setNewDraw({ ...newDraw, payees: updated });
  };
  
  // Handle invoice file upload for a payee
  const handleInvoiceUpload = async (index, file) => {
    if (!file) return;
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('folder', 'draw-request-invoices');
      
      const response = await axios.post(`${API}/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      const fileUrl = response.data.url || response.data.file_url;
      const fileType = file.type.includes('pdf') ? 'pdf' : 'image';
      
      updatePayee(index, 'attached_invoice_url', fileUrl);
      updatePayee(index, 'attached_invoice_name', file.name);
      updatePayee(index, 'attached_invoice_type', fileType);
      
      toast.success(`Invoice "${file.name}" attached`);
    } catch (error) {
      console.error('Error uploading invoice:', error);
      toast.error('Failed to upload invoice');
    }
  };
  
  const removeInvoiceAttachment = (index) => {
    updatePayee(index, 'attached_invoice_url', '');
    updatePayee(index, 'attached_invoice_name', '');
    updatePayee(index, 'attached_invoice_type', '');
  };
  
  // Pay Report functions
  const createPayReport = async () => {
    try {
      if (!newPayReport.title_company_name) {
        toast.error('Please select or enter a title company');
        return;
      }
      
      await axios.post(`${API}/projects/${projectId}/title-company-reports`, newPayReport);
      toast.success('Pay report added');
      setShowPayReportDialog(false);
      setNewPayReport({
        title_company_id: '',
        title_company_name: '',
        report_date: new Date().toISOString().split('T')[0],
        report_number: '',
        original_loan_amount: '',
        total_disbursed: '',
        remaining_balance: '',
        loan_status: 'active',
        draw_request_id: '',
        total_this_report: '',
        document_url: '',
        document_name: '',
        notes: ''
      });
      fetchData();
    } catch (error) {
      console.error('Error creating pay report:', error);
      toast.error('Failed to add pay report');
    }
  };
  
  const deletePayReport = async (reportId) => {
    try {
      await axios.delete(`${API}/title-company-reports/${reportId}`);
      toast.success('Pay report deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete pay report');
    }
  };
  
  const updatePayee = (index, field, value) => {
    const updated = [...newDraw.payees];
    updated[index] = { ...updated[index], [field]: value };
    setNewDraw({ ...newDraw, payees: updated });
  };
  
  const removePayee = (index) => {
    setNewDraw({
      ...newDraw,
      payees: newDraw.payees.filter((_, i) => i !== index)
    });
  };
  
  const addFromInvoice = (invoice) => {
    setNewDraw({
      ...newDraw,
      payees: [...newDraw.payees, {
        pay_to: invoice.vendor_name || invoice.bill_to?.name || 'See Invoice',
        mail_address: invoice.vendor_address || '',
        phone: invoice.vendor_phone || '',
        description: `Invoice #${invoice.invoice_number}: ${invoice.description || ''}`,
        amount: invoice.total || 0,
        invoice_id: invoice.id,
        invoice_number: invoice.invoice_number,
        has_subcontractors: false,
        lien_waiver_attached: false,
        receipt_attached: false
      }]
    });
  };

  const addFromReceivedInvoice = (ri) => {
    setNewDraw((prev) => prev.payees.some((p) => p.received_invoice_id === ri.id) ? prev : ({
      ...prev,
      payees: [...prev.payees, {
        pay_to: ri.vendor_name || 'See invoice',
        mail_address: ri.vendor_address || '',
        phone: ri.vendor_phone || '',
        email: ri.vendor_email || '',
        description: `Invoice #${ri.invoice_number || '—'}: ${ri.description || ''}`,
        amount: ri.amount || 0,
        received_invoice_id: ri.id,
        invoice_number: ri.invoice_number,
        has_subcontractors: false,
        lien_waiver_attached: false,
        receipt_attached: !!ri.document_url,
      }],
    }));
  };
  
  const signDrawRequest = async () => {
    try {
      await axios.post(`${API}/draw-requests/${selectedDraw.id}/sign`, signatureData);
      toast.success(`${signatureData.role === 'owner' ? 'Owner' : 'Contractor'} signature recorded`);
      setShowSignDialog(false);
      setSignatureData({ role: 'owner', name: '', signature: '', title: '' });
      fetchData();
    } catch (error) {
      toast.error('Failed to record signature');
    }
  };
  
  const senderSignDrawRequest = async (drawId) => {
    try {
      await axios.post(`${API}/draw-requests/${drawId}/sender-sign`, {
        name: signatureData.name || 'Contractor',
        title: signatureData.title,
        signature: 'typed'
      });
      toast.success('Your signature recorded - ready to send to client');
      setShowSignDialog(false);
      fetchData();
    } catch (error) {
      toast.error('Failed to record signature');
    }
  };
  
  const uploadSignedPdf = async () => {
    try {
      if (!uploadedSignedUrl) {
        toast.error('Please enter the signed PDF URL');
        return;
      }
      
      await axios.post(`${API}/draw-requests/${selectedDraw.id}/upload-signed`, {
        pdf_url: uploadedSignedUrl,
        owner_name: selectedDraw.owner_name
      });
      toast.success('Hand-signed PDF uploaded');
      setShowUploadSignedDialog(false);
      setUploadedSignedUrl('');
      fetchData();
    } catch (error) {
      toast.error('Failed to upload signed PDF');
    }
  };
  
  const submitToTitle = async (drawId) => {
    try {
      await axios.post(`${API}/draw-requests/${drawId}/submit-to-title`);
      toast.success('Draw request submitted to title company');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit');
    }
  };
  
  const markFunded = async (drawId) => {
    try {
      await axios.post(`${API}/draw-requests/${drawId}/mark-funded`, {});
      toast.success('Draw request marked as funded');
      fetchData();
    } catch (error) {
      toast.error('Failed to mark as funded');
    }
  };
  
  const deleteDraw = async (drawId) => {
    try {
      await axios.delete(`${API}/draw-requests/${drawId}`);
      toast.success('Draw request deleted');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete');
    }
  };
  
  const openPreview = async (draw) => {
    try {
      const res = await axios.get(`${API}/draw-requests/${draw.id}/pdf`);
      setPreviewHtml(res.data.html);
      setSelectedDraw(draw);
      setShowPreviewDialog(true);
    } catch (error) {
      toast.error('Failed to generate preview');
    }
  };
  
  const exportPDF = async (drawId) => {
    try {
      const res = await axios.get(`${API}/draw-requests/${drawId}/pdf`);
      
      // Open in new window for printing
      const printWindow = window.open('', '_blank');
      printWindow.document.write(res.data.html);
      printWindow.document.close();
      
      toast.success('PDF opened in new tab - use browser print to save/print');
    } catch (error) {
      toast.error('Failed to generate PDF');
    }
  };
  
  const emailDraw = async (drawId) => {
    const draw = drawRequests.find(d => d.id === drawId);
    const email = prompt('Enter email address to send draw request:');
    if (!email) return;
    
    try {
      await axios.post(`${API}/draw-requests/${drawId}/email`, {
        recipients: [email]
      });
      toast.success(`Draw request sent to ${email}`);
    } catch (error) {
      toast.error('Failed to send email');
    }
  };
  
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };
  
  const totalNewDraw = newDraw.payees.reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#BC5D41]"></div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${darkMode ? 'bg-[#0D0D0D] text-[#E5E2DD]' : 'bg-[#FAF9F7]'}`}>
      {/* Header */}
      <div className={`border-b ${darkMode ? 'border-[#1A1A1A] bg-[#0D0D0D]' : 'border-[#E5E2DD] bg-white'} sticky top-0 z-10`}>
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => navigate(`/projects/${projectId}/hub`)}
                className={darkMode ? 'text-[#8A8A8A] hover:text-[#E5E2DD]' : ''}
                data-testid="back-to-project-btn"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Project
              </Button>
              <div className="flex items-center gap-2">
                <HelpIcon elementId="draw-requests" position="inline" />
                <div>
                  <div className="flex items-center gap-2">
                    <h1 className="text-xl font-semibold">Draw Requests</h1>
                    <BlueprintIndicator featureId="draw_requests" size="sm" />
                  </div>
                  <p className="text-sm text-muted-foreground">{project?.name}</p>
                </div>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => setShowTitleCompanyDialog(true)}
                data-testid="title-companies-btn"
              >
                <Landmark className="w-4 h-4 mr-2" />
                Title Companies
              </Button>
              <Button 
                size="sm"
                className="bg-[#BC5D41] hover:bg-[#A34D35] text-white"
                onClick={() => setShowCreateDialog(true)}
                data-testid="new-draw-request-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                New Draw Request
              </Button>
            </div>
          </div>
        </div>
      </div>
      
      {/* Summary Cards */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className={`p-4 rounded-lg border ${darkMode ? 'border-[#1A1A1A] bg-[#0D0D0D]' : 'border-[#E5E2DD] bg-white'}`}>
            <div className="flex items-center gap-2 text-muted-foreground text-sm mb-1">
              <Receipt className="w-4 h-4" />
              Total Requested
            </div>
            <p className="text-2xl font-semibold">{formatCurrency(summary.total_requested)}</p>
          </div>
          
          <div className={`p-4 rounded-lg border ${darkMode ? 'border-[#1A1A1A] bg-[#0D0D0D]' : 'border-[#E5E2DD] bg-white'}`}>
            <div className="flex items-center gap-2 text-muted-foreground text-sm mb-1">
              <CheckCircle className="w-4 h-4" />
              Total Funded
            </div>
            <p className="text-2xl font-semibold text-green-600">{formatCurrency(summary.total_funded)}</p>
          </div>
          
          <div className={`p-4 rounded-lg border ${darkMode ? 'border-[#1A1A1A] bg-[#0D0D0D]' : 'border-[#E5E2DD] bg-white'}`}>
            <div className="flex items-center gap-2 text-muted-foreground text-sm mb-1">
              <PenTool className="w-4 h-4" />
              Pending Signature
            </div>
            <p className="text-2xl font-semibold text-amber-600">{formatCurrency(summary.pending_signature)}</p>
          </div>
          
          <div className={`p-4 rounded-lg border ${darkMode ? 'border-[#1A1A1A] bg-[#0D0D0D]' : 'border-[#E5E2DD] bg-white'}`}>
            <div className="flex items-center gap-2 text-muted-foreground text-sm mb-1">
              <Landmark className="w-4 h-4" />
              Pending Funding
            </div>
            <p className="text-2xl font-semibold text-purple-600">{formatCurrency(summary.pending_funding)}</p>
          </div>
        </div>
        
        {/* Tabs: Draw Requests | Pay Reports */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-4">
            <TabsTrigger value="draws" className="gap-2">
              <Receipt className="w-4 h-4" />
              Draw Requests
            </TabsTrigger>
            <TabsTrigger value="pay-reports" className="gap-2">
              <ClipboardList className="w-4 h-4" />
              Pay Reports
              {payReports.length > 0 && (
                <Badge variant="secondary" className="ml-1">{payReports.length}</Badge>
              )}
            </TabsTrigger>
          </TabsList>
          
          {/* Draw Requests Tab */}
          <TabsContent value="draws">
            <div className={`rounded-lg border ${darkMode ? 'border-[#1A1A1A] bg-[#0D0D0D]' : 'border-[#E5E2DD] bg-white'}`}>
              <div className="p-4 border-b border-inherit">
                <h2 className="font-medium">Draw Requests</h2>
              </div>
              
              {drawRequests.length === 0 ? (
                <div className="p-8 text-center text-muted-foreground">
                  <Receipt className="w-10 h-10 mx-auto mb-3 opacity-50" />
                  <p>No draw requests yet.</p>
                  <p className="text-sm">Create a draw request to bundle invoices for client authorization.</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Draw #</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead>Title Company</TableHead>
                      <TableHead>Payees</TableHead>
                      <TableHead className="text-right">Total</TableHead>
                      <TableHead>Signatures</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="w-[50px]"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {drawRequests.map((draw) => {
                      const StatusIcon = STATUS_CONFIG[draw.status]?.icon || Clock;
                      return (
                        <TableRow key={draw.id} data-testid={`draw-request-row-${draw.draw_number}`}>
                          <TableCell className="font-medium">#{draw.draw_number}</TableCell>
                          <TableCell>{draw.date_submitted}</TableCell>
                          <TableCell className="text-sm">
                            {draw.title_company_name || '-'}
                        {draw.lender_name && (
                          <span className="block text-xs text-muted-foreground">{draw.lender_name}</span>
                        )}
                      </TableCell>
                      <TableCell>{draw.payees?.length || 0} items</TableCell>
                      <TableCell className="text-right font-semibold">{formatCurrency(draw.total_draw)}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1" title="Sender | Owner">
                          <div className={`w-2 h-2 rounded-full ${draw.sender_signed_at ? 'bg-green-500' : 'bg-gray-300'}`} title="Sender" />
                          <div className={`w-2 h-2 rounded-full ${draw.owner_signed_at ? 'bg-green-500' : 'bg-gray-300'}`} title="Owner" />
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge className={STATUS_CONFIG[draw.status]?.color}>
                          <StatusIcon className="w-3 h-3 mr-1" />
                          {STATUS_CONFIG[draw.status]?.label}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm" data-testid={`draw-actions-${draw.draw_number}`}>
                              <MoreVertical className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => openPreview(draw)}>
                              <Eye className="w-4 h-4 mr-2" />
                              Preview
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => setSelectedDraw(draw)}>
                              <FileText className="w-4 h-4 mr-2" />
                              View Details
                            </DropdownMenuItem>
                            
                            <DropdownMenuSeparator />
                            
                            {/* Sender Sign (you sign first) */}
                            {draw.status === 'draft' && !draw.sender_signed_at && (
                              <DropdownMenuItem onClick={() => {
                                setSelectedDraw(draw);
                                setSignatureData({ ...signatureData, role: 'sender' });
                                setShowSignDialog(true);
                              }}>
                                <PenTool className="w-4 h-4 mr-2" />
                                Sign as Sender
                              </DropdownMenuItem>
                            )}
                            
                            {/* Owner Sign (after sender) */}
                            {(draw.status === 'draft' || draw.status === 'pending_signature') && (
                              <DropdownMenuItem onClick={() => {
                                setSelectedDraw(draw);
                                setSignatureData({ ...signatureData, role: 'owner' });
                                setShowSignDialog(true);
                              }}>
                                <PenTool className="w-4 h-4 mr-2" />
                                Sign as Owner
                              </DropdownMenuItem>
                            )}
                            
                            {/* Upload Hand-Signed PDF */}
                            {(draw.status === 'draft' || draw.status === 'pending_signature') && (
                              <DropdownMenuItem onClick={() => {
                                setSelectedDraw(draw);
                                setShowUploadSignedDialog(true);
                              }}>
                                <Upload className="w-4 h-4 mr-2" />
                                Upload Hand-Signed PDF
                              </DropdownMenuItem>
                            )}
                            
                            {draw.status === 'signed' && (
                              <DropdownMenuItem onClick={() => submitToTitle(draw.id)}>
                                <Send className="w-4 h-4 mr-2" />
                                Submit to Title Co.
                              </DropdownMenuItem>
                            )}
                            {draw.status === 'submitted_to_title' && (
                              <DropdownMenuItem onClick={() => markFunded(draw.id)}>
                                <DollarSign className="w-4 h-4 mr-2" />
                                Mark Funded
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuSeparator />
                            <DropdownMenuItem onClick={() => exportPDF(draw.id)}>
                              <Printer className="w-4 h-4 mr-2" />
                              Print / Export PDF
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => emailDraw(draw.id)}>
                              <Mail className="w-4 h-4 mr-2" />
                              Email Draw Request
                            </DropdownMenuItem>
                            {draw.status === 'draft' && (
                              <DropdownMenuItem 
                                className="text-red-600"
                                onClick={() => deleteDraw(draw.id)}
                              >
                                <Trash2 className="w-4 h-4 mr-2" />
                                Delete
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
            </div>
          </TabsContent>
          
          {/* Pay Reports Tab */}
          <TabsContent value="pay-reports">
            <div className={`rounded-lg border ${darkMode ? 'border-[#1A1A1A] bg-[#0D0D0D]' : 'border-[#E5E2DD] bg-white'}`}>
              <div className="p-4 border-b border-inherit flex justify-between items-center">
                <div>
                  <h2 className="font-medium">Title Company Pay Reports</h2>
                  <p className="text-sm text-muted-foreground">Track payments disbursed and loan status</p>
                </div>
                <Button 
                  size="sm"
                  className="bg-[#BC5D41] hover:bg-[#A34D35] text-white"
                  onClick={() => setShowPayReportDialog(true)}
                  data-testid="add-pay-report-btn"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Pay Report
                </Button>
              </div>
              
              {payReports.length === 0 ? (
                <div className="p-8 text-center text-muted-foreground">
                  <ClipboardList className="w-10 h-10 mx-auto mb-3 opacity-50" />
                  <p>No pay reports yet.</p>
                  <p className="text-sm">Add reports received from the title company showing payments and loan status.</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>Title Company</TableHead>
                      <TableHead>Report #</TableHead>
                      <TableHead className="text-right">This Report</TableHead>
                      <TableHead className="text-right">Total Disbursed</TableHead>
                      <TableHead className="text-right">Remaining</TableHead>
                      <TableHead>Loan Status</TableHead>
                      <TableHead>Document</TableHead>
                      <TableHead className="w-[50px]"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {payReports.map((report) => (
                      <TableRow key={report.id}>
                        <TableCell>{report.report_date}</TableCell>
                        <TableCell>{report.title_company_name || '-'}</TableCell>
                        <TableCell>{report.report_number || '-'}</TableCell>
                        <TableCell className="text-right font-medium">{formatCurrency(report.total_this_report)}</TableCell>
                        <TableCell className="text-right text-green-600">{formatCurrency(report.total_disbursed)}</TableCell>
                        <TableCell className="text-right">{formatCurrency(report.remaining_balance)}</TableCell>
                        <TableCell>
                          <Badge variant={report.loan_status === 'active' ? 'default' : report.loan_status === 'closed' ? 'secondary' : 'destructive'}>
                            {report.loan_status}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {report.document_url ? (
                            <a 
                              href={report.document_url} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="flex items-center gap-1 text-xs text-[#BC5D41] hover:underline"
                            >
                              <Paperclip className="w-3 h-3" />
                              View
                            </a>
                          ) : (
                            <span className="text-xs text-muted-foreground">-</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm">
                                <MoreVertical className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              {report.document_url && (
                                <DropdownMenuItem onClick={() => window.open(report.document_url, '_blank')}>
                                  <Eye className="w-4 h-4 mr-2" />
                                  View Document
                                </DropdownMenuItem>
                              )}
                              <DropdownMenuSeparator />
                              <DropdownMenuItem 
                                className="text-red-600"
                                onClick={() => deletePayReport(report.id)}
                              >
                                <Trash2 className="w-4 h-4 mr-2" />
                                Delete
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
              
              {/* Pay Reports Summary */}
              {payReports.length > 0 && (
                <div className={`p-4 border-t ${darkMode ? 'border-[#1A1A1A] bg-[#0A0A0A]' : 'border-[#E5E2DD] bg-gray-50'}`}>
                  <div className="flex justify-between items-center">
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <TrendingUp className="w-4 h-4" />
                      <span>{payReportsSummary.total_reports || 0} reports received</span>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-muted-foreground">Total Disbursed to Date</p>
                      <p className="text-xl font-semibold text-green-600">{formatCurrency(payReportsSummary.total_disbursed)}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>
      
      {/* Create Draw Request Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>New Draw Request</DialogTitle>
            <DialogDescription>
              Bundle invoices for client authorization and title company funding
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6">
            {/* Project & Party Info */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Owner / Borrower Name *</Label>
                <Input 
                  value={newDraw.owner_name}
                  onChange={(e) => setNewDraw({ ...newDraw, owner_name: e.target.value })}
                  placeholder="Client/Owner name"
                />
              </div>
              <div>
                <Label>Contractor Name</Label>
                <Input 
                  value={newDraw.contractor_name}
                  onChange={(e) => setNewDraw({ ...newDraw, contractor_name: e.target.value })}
                  placeholder="Your company name"
                />
              </div>
            </div>
            
            {/* Title Company Selection */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Title Company</Label>
                <Select 
                  value={newDraw.title_company_id} 
                  onValueChange={(v) => setNewDraw({ ...newDraw, title_company_id: v })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select title company..." />
                  </SelectTrigger>
                  <SelectContent>
                    {titleCompanies.map(tc => (
                      <SelectItem key={tc.id} value={tc.id}>
                        {tc.name} {tc.is_default && '(Default)'}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Payment Type</Label>
                <Select 
                  value={newDraw.payment_type} 
                  onValueChange={(v) => setNewDraw({ ...newDraw, payment_type: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="title_company">Via Title Company (Bank)</SelectItem>
                    <SelectItem value="direct">Direct from Client</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            {/* Quick Add from Invoices */}
            {invoices.filter(i => i.status !== 'paid').length > 0 && (
              <div>
                <Label className="mb-2 block">Quick Add from Unpaid Invoices</Label>
                <div className="flex flex-wrap gap-2">
                  {invoices.filter(i => i.status !== 'paid').slice(0, 5).map(inv => (
                    <Button
                      key={inv.id}
                      variant="outline"
                      size="sm"
                      onClick={() => addFromInvoice(inv)}
                      disabled={newDraw.payees.some(p => p.invoice_id === inv.id)}
                    >
                      #{inv.invoice_number} - {formatCurrency(inv.total)}
                    </Button>
                  ))}
                </div>
              </div>
            )}
            {receivedInvoices.length > 0 && (
              <div>
                <Label className="mb-2 block">Quick Add from Received Invoices (vendor and trade bills)</Label>
                <div className="flex flex-wrap gap-2">
                  {receivedInvoices.slice(0, 8).map((ri) => (
                    <Button key={ri.id} variant="outline" size="sm" onClick={() => addFromReceivedInvoice(ri)}
                      disabled={newDraw.payees.some((p) => p.received_invoice_id === ri.id)} data-testid={`draw-add-ri-${ri.id}`}>
                      {ri.vendor_name} · #{ri.invoice_number || '—'} · {formatCurrency(ri.amount)}
                    </Button>
                  ))}
                </div>
              </div>
            )}
            
            {/* Payees Table - Expanded */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label>Payees / Invoices</Label>
                <Button variant="outline" size="sm" onClick={addPayeeRow} data-testid="add-payee-btn">
                  <Plus className="w-4 h-4 mr-1" />
                  Add Payee
                </Button>
              </div>
              
              {newDraw.payees.length === 0 ? (
                <div className={`p-8 text-center border-2 border-dashed rounded-lg ${darkMode ? 'border-[#1A1A1A]' : 'border-gray-200'}`}>
                  <p className="text-muted-foreground">No payees added. Click "Add Payee" or select from invoices above.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {newDraw.payees.map((payee, index) => (
                    <div key={index} className={`p-4 rounded-lg border ${darkMode ? 'border-[#1A1A1A] bg-[#0A0A0A]' : 'border-gray-200 bg-gray-50'}`}>
                      <div className="flex justify-between items-start mb-3">
                        <h4 className="font-medium">Payee #{index + 1}</h4>
                        <Button variant="ghost" size="sm" onClick={() => removePayee(index)}>
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                      
                      {/* Contractor Selection - for Insurance Audit linking */}
                      {contractors.length > 0 && (
                        <div className="mb-3">
                          <Label className="text-xs">Link to Existing Contractor (for Insurance Audit)</Label>
                          <Select 
                            value={payee.contractor_id || 'none'} 
                            onValueChange={(val) => selectContractorForPayee(index, val === 'none' ? '' : val)}
                          >
                            <SelectTrigger className="w-full" data-testid={`payee-contractor-select-${index}`}>
                              <SelectValue placeholder="Select contractor to auto-fill..." />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none">-- Enter manually --</SelectItem>
                              {contractors.map(c => (
                                <SelectItem key={c.id} value={c.id}>
                                  {c.company_name} {c.trade ? `(${c.trade})` : ''}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          {payee.contractor_id && (
                            <p className="text-xs text-green-600 mt-1">✓ Linked to contractor for insurance tracking</p>
                          )}
                        </div>
                      )}
                      
                      <div className="grid grid-cols-3 gap-3">
                        {/* Company & Contact */}
                        <div>
                          <Label className="text-xs">Company / Pay To *</Label>
                          <Input 
                            placeholder="Vendor name"
                            value={payee.pay_to}
                            onChange={(e) => updatePayee(index, 'pay_to', e.target.value)}
                          />
                        </div>
                        <div>
                          <Label className="text-xs">Contact Name</Label>
                          <Input 
                            placeholder="Contact person"
                            value={payee.contact_name || ''}
                            onChange={(e) => updatePayee(index, 'contact_name', e.target.value)}
                          />
                        </div>
                        <div>
                          <Label className="text-xs">Phone</Label>
                          <Input 
                            placeholder="(555) 555-5555"
                            value={payee.phone || ''}
                            onChange={(e) => updatePayee(index, 'phone', e.target.value)}
                          />
                        </div>
                        
                        {/* Address */}
                        <div className="col-span-2">
                          <Label className="text-xs">Street Address</Label>
                          <Input 
                            placeholder="123 Main St"
                            value={payee.mail_address || ''}
                            onChange={(e) => updatePayee(index, 'mail_address', e.target.value)}
                          />
                        </div>
                        <div>
                          <Label className="text-xs">Email</Label>
                          <Input 
                            placeholder="email@example.com"
                            value={payee.email || ''}
                            onChange={(e) => updatePayee(index, 'email', e.target.value)}
                          />
                        </div>
                        
                        <div>
                          <Label className="text-xs">City</Label>
                          <Input 
                            placeholder="City"
                            value={payee.city || ''}
                            onChange={(e) => updatePayee(index, 'city', e.target.value)}
                          />
                        </div>
                        <div>
                          <Label className="text-xs">State</Label>
                          <Input 
                            placeholder="MO"
                            value={payee.state || ''}
                            onChange={(e) => updatePayee(index, 'state', e.target.value)}
                          />
                        </div>
                        <div>
                          <Label className="text-xs">ZIP</Label>
                          <Input 
                            placeholder="65801"
                            value={payee.zip_code || ''}
                            onChange={(e) => updatePayee(index, 'zip_code', e.target.value)}
                          />
                        </div>
                        
                        {/* Description & Amount */}
                        <div className="col-span-2">
                          <Label className="text-xs">Description of Work *</Label>
                          <Input 
                            placeholder="Description of work completed"
                            value={payee.description}
                            onChange={(e) => updatePayee(index, 'description', e.target.value)}
                          />
                        </div>
                        <div>
                          <Label className="text-xs">Amount *</Label>
                          <Input 
                            type="number"
                            placeholder="0.00"
                            value={payee.amount || ''}
                            onChange={(e) => updatePayee(index, 'amount', e.target.value)}
                          />
                        </div>
                        
                        {/* Delivery & Options */}
                        <div>
                          <Label className="text-xs">Check Delivery</Label>
                          <Select 
                            value={payee.delivery_method || 'mail'} 
                            onValueChange={(v) => updatePayee(index, 'delivery_method', v)}
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="mail">Mail Check</SelectItem>
                              <SelectItem value="pickup">Pickup</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="flex items-center gap-4 col-span-2">
                          <div className="flex items-center gap-2">
                            <Checkbox 
                              checked={payee.lien_waiver_attached}
                              onCheckedChange={(v) => updatePayee(index, 'lien_waiver_attached', v)}
                            />
                            <Label className="text-xs mb-0">Lien Waiver</Label>
                          </div>
                          <div className="flex items-center gap-2">
                            <Checkbox 
                              checked={payee.receipt_attached}
                              onCheckedChange={(v) => updatePayee(index, 'receipt_attached', v)}
                            />
                            <Label className="text-xs mb-0">Receipt Attached</Label>
                          </div>
                          <div className="flex items-center gap-2">
                            <Checkbox 
                              checked={payee.has_subcontractors}
                              onCheckedChange={(v) => updatePayee(index, 'has_subcontractors', v)}
                            />
                            <Label className="text-xs mb-0">Has Subcontractors</Label>
                          </div>
                        </div>
                        
                        {/* Invoice Attachment */}
                        <div className="col-span-3 mt-2 pt-2 border-t border-dashed">
                          <Label className="text-xs mb-2 block">Attach Payee's Invoice</Label>
                          {payee.attached_invoice_url ? (
                            <div className={`flex items-center gap-2 p-2 rounded-lg ${darkMode ? 'bg-[#1A1A1A]' : 'bg-green-50 border border-green-200'}`}>
                              <Paperclip className="w-4 h-4 text-green-600 flex-shrink-0" />
                              <a 
                                href={payee.attached_invoice_url} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="text-sm text-green-700 hover:underline flex-1 truncate"
                              >
                                {payee.attached_invoice_name || 'Invoice attached'}
                              </a>
                              <Badge variant="outline" className="text-xs">
                                {payee.attached_invoice_type === 'pdf' ? 'PDF' : 'Image'}
                              </Badge>
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                className="h-6 w-6 p-0"
                                onClick={() => removeInvoiceAttachment(index)}
                              >
                                <X className="w-4 h-4 text-red-500" />
                              </Button>
                            </div>
                          ) : (
                            <div className={`flex items-center gap-2`}>
                              <label className={`flex items-center gap-2 px-3 py-2 rounded-lg border-2 border-dashed cursor-pointer hover:border-[#BC5D41] transition-colors ${darkMode ? 'border-[#2A2A2A] hover:bg-[#1A1A1A]' : 'border-gray-200 hover:bg-gray-50'}`}>
                                <Paperclip className="w-4 h-4 text-muted-foreground" />
                                <span className="text-sm text-muted-foreground">Click to attach invoice (PDF or image)</span>
                                <input 
                                  type="file"
                                  accept=".pdf,.png,.jpg,.jpeg,.webp"
                                  className="hidden"
                                  onChange={(e) => handleInvoiceUpload(index, e.target.files[0])}
                                  data-testid={`payee-invoice-upload-${index}`}
                                />
                              </label>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            {/* Total */}
            <div className="flex justify-end border-t pt-4">
              <div className="text-right">
                <p className="text-sm text-muted-foreground">Total Draw Amount</p>
                <p className="text-2xl font-semibold">{formatCurrency(totalNewDraw)}</p>
              </div>
            </div>
            
            {/* Disclaimer */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label>Disclaimer / Authorization Text</Label>
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={() => setNewDraw({ ...newDraw, disclaimer: defaultDisclaimer })}
                >
                  Reset to Default
                </Button>
              </div>
              <Textarea 
                placeholder={defaultDisclaimer || "Enter disclaimer text..."}
                value={newDraw.disclaimer}
                onChange={(e) => setNewDraw({ ...newDraw, disclaimer: e.target.value })}
                rows={4}
                className="text-xs"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Leave blank to use company default. This appears on the PDF.
              </p>
            </div>
            
            {/* Notes */}
            <div>
              <Label>Internal Notes</Label>
              <Textarea 
                placeholder="Additional notes (not shown on PDF)..."
                value={newDraw.notes}
                onChange={(e) => setNewDraw({ ...newDraw, notes: e.target.value })}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>Cancel</Button>
            <Button 
              className="bg-[#BC5D41] hover:bg-[#A34D35] text-white"
              onClick={createDrawRequest}
              disabled={newDraw.payees.length === 0}
              data-testid="create-draw-request-submit"
            >
              <Plus className="w-4 h-4 mr-2" />
              Create Draw Request
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Title Company Dialog */}
      <Dialog open={showTitleCompanyDialog} onOpenChange={setShowTitleCompanyDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Title Companies</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Existing Title Companies */}
            {titleCompanies.length > 0 && (
              <div className="space-y-2">
                {titleCompanies.map(tc => (
                  <div 
                    key={tc.id}
                    className={`p-3 rounded-lg border ${darkMode ? 'border-[#1A1A1A]' : 'border-[#E5E2DD]'}`}
                  >
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="font-medium">{tc.name}</p>
                        {tc.lender_name && (
                          <p className="text-sm text-muted-foreground">Lender: {tc.lender_name}</p>
                        )}
                        {tc.phone && (
                          <p className="text-sm text-muted-foreground">{tc.phone}</p>
                        )}
                      </div>
                      {tc.is_default && (
                        <Badge variant="outline">Default</Badge>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
            
            {/* Add New */}
            <div className={`p-4 rounded-lg border-2 border-dashed ${darkMode ? 'border-[#1A1A1A]' : 'border-[#E5E2DD]'}`}>
              <h4 className="font-medium mb-3">Add New Title Company</h4>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Company Name *</Label>
                  <Input 
                    value={newTitleCompany.name}
                    onChange={(e) => setNewTitleCompany({ ...newTitleCompany, name: e.target.value })}
                    placeholder="Titan Title & Closing"
                  />
                </div>
                <div>
                  <Label>Lender/Bank</Label>
                  <Input 
                    value={newTitleCompany.lender_name}
                    onChange={(e) => setNewTitleCompany({ ...newTitleCompany, lender_name: e.target.value })}
                    placeholder="State Bank"
                  />
                </div>
                <div>
                  <Label>Phone</Label>
                  <Input 
                    value={newTitleCompany.phone}
                    onChange={(e) => setNewTitleCompany({ ...newTitleCompany, phone: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Email</Label>
                  <Input 
                    value={newTitleCompany.email}
                    onChange={(e) => setNewTitleCompany({ ...newTitleCompany, email: e.target.value })}
                  />
                </div>
                <div className="col-span-2">
                  <Label>Address</Label>
                  <Input 
                    value={newTitleCompany.address}
                    onChange={(e) => setNewTitleCompany({ ...newTitleCompany, address: e.target.value })}
                  />
                </div>
                <div className="col-span-2 flex items-center gap-2">
                  <Checkbox 
                    checked={newTitleCompany.is_default}
                    onCheckedChange={(v) => setNewTitleCompany({ ...newTitleCompany, is_default: v })}
                  />
                  <Label className="mb-0">Set as default</Label>
                </div>
              </div>
              <Button 
                className="mt-3"
                onClick={createTitleCompany}
                disabled={!newTitleCompany.name}
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Title Company
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Sign Dialog - Enhanced */}
      <Dialog open={showSignDialog} onOpenChange={setShowSignDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {signatureData.role === 'sender' 
                ? `Sign as Sender - Draw #${selectedDraw?.draw_number}`
                : `Sign Draw Request #${selectedDraw?.draw_number}`
              }
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {signatureData.role !== 'sender' && (
              <div>
                <Label>Signing as</Label>
                <Select 
                  value={signatureData.role} 
                  onValueChange={(v) => setSignatureData({ ...signatureData, role: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="owner">Owner / Borrower</SelectItem>
                    <SelectItem value="contractor">Contractor</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
            
            <div>
              <Label>Your Full Legal Name *</Label>
              <Input 
                value={signatureData.name}
                onChange={(e) => setSignatureData({ ...signatureData, name: e.target.value })}
                placeholder="Full legal name"
                data-testid="signature-name-input"
              />
            </div>
            
            {signatureData.role === 'sender' && (
              <div>
                <Label>Title</Label>
                <Input 
                  value={signatureData.title}
                  onChange={(e) => setSignatureData({ ...signatureData, title: e.target.value })}
                  placeholder="e.g., Managing Member"
                />
              </div>
            )}
            
            <div className={`p-4 rounded-lg ${darkMode ? 'bg-[#1A1A1A]' : 'bg-gray-50'}`}>
              <p className="text-sm text-muted-foreground mb-2">
                {signatureData.role === 'sender' 
                  ? 'By signing, I certify that the information in this draw request is accurate and request authorization for payment.'
                  : `By signing, I authorize the payment of ${formatCurrency(selectedDraw?.total_draw)} for the invoices listed in this draw request.`
                }
              </p>
              <p className="text-xs text-muted-foreground">
                Signature timestamp: {new Date().toLocaleString()}
              </p>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSignDialog(false)}>Cancel</Button>
            <Button 
              className="bg-[#BC5D41] hover:bg-[#A34D35] text-white"
              onClick={() => {
                if (signatureData.role === 'sender') {
                  senderSignDrawRequest(selectedDraw.id);
                } else {
                  signDrawRequest();
                }
              }}
              disabled={!signatureData.name}
              data-testid="sign-submit-btn"
            >
              <PenTool className="w-4 h-4 mr-2" />
              {signatureData.role === 'sender' ? 'Sign & Prepare for Client' : 'Sign & Submit'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Upload Hand-Signed PDF Dialog */}
      <Dialog open={showUploadSignedDialog} onOpenChange={setShowUploadSignedDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Upload Hand-Signed PDF</DialogTitle>
            <DialogDescription>
              Upload a scanned or photographed copy of the signed draw request
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>Signed PDF URL *</Label>
              <Input 
                value={uploadedSignedUrl}
                onChange={(e) => setUploadedSignedUrl(e.target.value)}
                placeholder="https://... or paste file URL"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Upload your signed PDF to cloud storage and paste the URL here
              </p>
            </div>
            
            <div className={`p-4 rounded-lg ${darkMode ? 'bg-[#1A1A1A]' : 'bg-gray-50'}`}>
              <p className="text-sm text-muted-foreground">
                This will mark the draw request as signed and create an alert for you to acknowledge.
              </p>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowUploadSignedDialog(false)}>Cancel</Button>
            <Button 
              className="bg-[#BC5D41] hover:bg-[#A34D35] text-white"
              onClick={uploadSignedPdf}
              disabled={!uploadedSignedUrl}
            >
              <Upload className="w-4 h-4 mr-2" />
              Upload & Mark Signed
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Preview Dialog */}
      <Dialog open={showPreviewDialog} onOpenChange={setShowPreviewDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <span>Draw Request Preview #{selectedDraw?.draw_number}</span>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => exportPDF(selectedDraw?.id)}>
                  <Printer className="w-4 h-4 mr-2" />
                  Print
                </Button>
                <Button variant="outline" size="sm" onClick={() => {
                  const newWindow = window.open('', '_blank');
                  newWindow.document.write(previewHtml);
                  newWindow.document.close();
                }}>
                  <ExternalLink className="w-4 h-4 mr-2" />
                  Open in New Tab
                </Button>
              </div>
            </DialogTitle>
          </DialogHeader>
          
          <div className={`overflow-auto max-h-[70vh] border rounded-lg ${darkMode ? 'bg-[#1A1A1A] border-[#2A2A2A]' : 'bg-white'}`}>
            <iframe
              srcDoc={previewHtml}
              className="w-full h-[600px] border-0"
              title="Draw Request Preview"
            />
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Draw Details Dialog */}
      <Dialog open={!!selectedDraw && !showSignDialog && !showPreviewDialog && !showUploadSignedDialog} onOpenChange={() => setSelectedDraw(null)}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              Draw Request #{selectedDraw?.draw_number}
              {selectedDraw?.uploaded_signed_pdf_url && (
                <Badge variant="outline" className="ml-2">
                  <FileUp className="w-3 h-3 mr-1" />
                  Hand-Signed
                </Badge>
              )}
            </DialogTitle>
          </DialogHeader>
          
          {selectedDraw && (
            <div className="space-y-4">
              {/* Header Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">Date Submitted</p>
                  <p className="font-medium">{selectedDraw.date_submitted}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Title Company</p>
                  <p className="font-medium">{selectedDraw.title_company_name || 'N/A'}</p>
                  {selectedDraw.lender_name && (
                    <p className="text-sm text-muted-foreground">Lender: {selectedDraw.lender_name}</p>
                  )}
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Property</p>
                  <p className="font-medium">{selectedDraw.property_address || selectedDraw.project_name}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Owner</p>
                  <p className="font-medium">{selectedDraw.owner_name || 'N/A'}</p>
                </div>
              </div>
              
              {/* Payees */}
              <div>
                <h4 className="font-medium mb-2">Payees / Invoices</h4>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Pay To</TableHead>
                      <TableHead>Description</TableHead>
                      <TableHead>Invoice</TableHead>
                      <TableHead>Delivery</TableHead>
                      <TableHead className="text-right">Amount</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {selectedDraw.payees?.map((payee, index) => (
                      <TableRow key={index}>
                        <TableCell>
                          <div>
                            <p className="font-medium">{payee.pay_to}</p>
                            {payee.contact_name && <p className="text-xs text-muted-foreground">{payee.contact_name}</p>}
                          </div>
                        </TableCell>
                        <TableCell>{payee.description}</TableCell>
                        <TableCell>
                          {payee.attached_invoice_url ? (
                            <a 
                              href={payee.attached_invoice_url} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="flex items-center gap-1 text-xs text-green-600 hover:underline"
                            >
                              <Paperclip className="w-3 h-3" />
                              {payee.attached_invoice_name || 'View'}
                            </a>
                          ) : (
                            <span className="text-xs text-muted-foreground">-</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className="text-xs">
                            {payee.delivery_method === 'pickup' ? 'Pickup' : 'Mail'}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">{formatCurrency(payee.amount)}</TableCell>
                      </TableRow>
                    ))}
                    <TableRow className="font-semibold">
                      <TableCell colSpan={4}>TOTAL DRAW</TableCell>
                      <TableCell className="text-right">{formatCurrency(selectedDraw.total_draw)}</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </div>
              
              {/* Signatures */}
              <div className={`p-4 rounded-lg ${darkMode ? 'bg-[#1A1A1A]' : 'bg-gray-50'}`}>
                <h4 className="font-medium mb-3">Signatures</h4>
                <div className="grid grid-cols-2 gap-4">
                  {/* Sender Signature */}
                  <div className="flex items-center gap-2">
                    {selectedDraw.sender_signed_at ? (
                      <CheckCircle2 className="w-5 h-5 text-green-500" />
                    ) : (
                      <AlertCircle className="w-5 h-5 text-gray-400" />
                    )}
                    <div>
                      <p className="text-sm font-medium">Sender (You)</p>
                      <p className="text-xs text-muted-foreground">
                        {selectedDraw.sender_signed_at 
                          ? `${selectedDraw.sender_signed_name} - ${new Date(selectedDraw.sender_signed_at).toLocaleString()}`
                          : 'Pending'}
                      </p>
                    </div>
                  </div>
                  
                  {/* Owner Signature */}
                  <div className="flex items-center gap-2">
                    {selectedDraw.owner_signed_at ? (
                      <CheckCircle2 className="w-5 h-5 text-green-500" />
                    ) : (
                      <AlertCircle className="w-5 h-5 text-gray-400" />
                    )}
                    <div>
                      <p className="text-sm font-medium">Owner / Borrower</p>
                      <p className="text-xs text-muted-foreground">
                        {selectedDraw.owner_signed_at 
                          ? `${selectedDraw.owner_signed_name} - ${new Date(selectedDraw.owner_signed_at).toLocaleString()}`
                          : 'Pending'}
                      </p>
                    </div>
                  </div>
                </div>
                
                {/* Hand-signed PDF link */}
                {selectedDraw.uploaded_signed_pdf_url && (
                  <div className="mt-3 pt-3 border-t">
                    <a 
                      href={selectedDraw.uploaded_signed_pdf_url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-sm text-[#BC5D41] hover:underline flex items-center gap-1"
                    >
                      <FileUp className="w-4 h-4" />
                      View Hand-Signed PDF
                    </a>
                  </div>
                )}
              </div>
              
              {/* Quick Actions */}
              <div className="flex gap-2 pt-4 border-t">
                <Button variant="outline" size="sm" onClick={() => openPreview(selectedDraw)}>
                  <Eye className="w-4 h-4 mr-2" />
                  Preview PDF
                </Button>
                <Button variant="outline" size="sm" onClick={() => exportPDF(selectedDraw.id)}>
                  <Printer className="w-4 h-4 mr-2" />
                  Print
                </Button>
                <Button variant="outline" size="sm" onClick={() => emailDraw(selectedDraw.id)}>
                  <Mail className="w-4 h-4 mr-2" />
                  Email
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      
      {/* Add Pay Report Dialog */}
      <Dialog open={showPayReportDialog} onOpenChange={setShowPayReportDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Add Title Company Pay Report</DialogTitle>
            <DialogDescription>
              Record a pay report received from the title company showing disbursements and loan status
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Title Company Selection */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Title Company</Label>
                <Select 
                  value={newPayReport.title_company_id} 
                  onValueChange={(v) => {
                    const tc = titleCompanies.find(t => t.id === v);
                    setNewPayReport({ 
                      ...newPayReport, 
                      title_company_id: v,
                      title_company_name: tc?.name || ''
                    });
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select title company..." />
                  </SelectTrigger>
                  <SelectContent>
                    {titleCompanies.map(tc => (
                      <SelectItem key={tc.id} value={tc.id}>
                        {tc.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Report Date</Label>
                <Input 
                  type="date"
                  value={newPayReport.report_date}
                  onChange={(e) => setNewPayReport({ ...newPayReport, report_date: e.target.value })}
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Report Number</Label>
                <Input 
                  value={newPayReport.report_number}
                  onChange={(e) => setNewPayReport({ ...newPayReport, report_number: e.target.value })}
                  placeholder="e.g., PR-001"
                />
              </div>
              <div>
                <Label>Linked Draw Request</Label>
                <Select 
                  value={newPayReport.draw_request_id} 
                  onValueChange={(v) => {
                    const dr = drawRequests.find(d => d.id === v);
                    setNewPayReport({ 
                      ...newPayReport, 
                      draw_request_id: v,
                      draw_request_number: dr?.draw_number
                    });
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Link to draw request (optional)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {drawRequests.map(dr => (
                      <SelectItem key={dr.id} value={dr.id}>
                        Draw #{dr.draw_number} - {formatCurrency(dr.total_draw)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            {/* Loan & Disbursement Info */}
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>Original Loan Amount</Label>
                <Input 
                  type="number"
                  value={newPayReport.original_loan_amount}
                  onChange={(e) => setNewPayReport({ ...newPayReport, original_loan_amount: e.target.value })}
                  placeholder="0.00"
                />
              </div>
              <div>
                <Label>Total Disbursed to Date</Label>
                <Input 
                  type="number"
                  value={newPayReport.total_disbursed}
                  onChange={(e) => setNewPayReport({ ...newPayReport, total_disbursed: e.target.value })}
                  placeholder="0.00"
                />
              </div>
              <div>
                <Label>Remaining Balance</Label>
                <Input 
                  type="number"
                  value={newPayReport.remaining_balance}
                  onChange={(e) => setNewPayReport({ ...newPayReport, remaining_balance: e.target.value })}
                  placeholder="0.00"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>This Report's Disbursement</Label>
                <Input 
                  type="number"
                  value={newPayReport.total_this_report}
                  onChange={(e) => setNewPayReport({ ...newPayReport, total_this_report: e.target.value })}
                  placeholder="Amount paid this report"
                />
              </div>
              <div>
                <Label>Loan Status</Label>
                <Select 
                  value={newPayReport.loan_status} 
                  onValueChange={(v) => setNewPayReport({ ...newPayReport, loan_status: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="closed">Closed</SelectItem>
                    <SelectItem value="defaulted">Defaulted</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            {/* Attach Report Document */}
            <div>
              <Label>Attach Pay Report Document</Label>
              <DragDropUpload
                value={newPayReport.document_url}
                fileName={newPayReport.document_name}
                onUpload={(url, name, type) => setNewPayReport({ 
                  ...newPayReport, 
                  document_url: url, 
                  document_name: name 
                })}
                onRemove={() => setNewPayReport({ 
                  ...newPayReport, 
                  document_url: '', 
                  document_name: '' 
                })}
                folder="title-company-reports"
                placeholder="Drag and drop or click to upload pay report"
                darkMode={darkMode}
              />
            </div>
            
            {/* Notes */}
            <div>
              <Label>Notes</Label>
              <Textarea 
                value={newPayReport.notes}
                onChange={(e) => setNewPayReport({ ...newPayReport, notes: e.target.value })}
                placeholder="Additional notes..."
                rows={2}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPayReportDialog(false)}>Cancel</Button>
            <Button 
              className="bg-[#BC5D41] hover:bg-[#A34D35] text-white"
              onClick={createPayReport}
              disabled={!newPayReport.title_company_name}
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Pay Report
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
